import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  UserCheck,
  FilePlus,
  BookOpen,
  Hash,
  Landmark,
  Receipt,
  Shield,
  CalendarCheck,
  Star,
  Sun,
  Moon,
  Mail,
  X,
  Sparkles,
  Clock,
  DollarSign,
  ChevronDown,
  ShieldCheck,
} from "lucide-react";
import { landingHighlights } from "../data/faqs";
import FounderStory from "../components/landing/FounderStory";
import LeadMagnetSection from "../components/landing/LeadMagnetSection";
import LeadMagnetPopup from "../components/landing/LeadMagnetPopup";
import HeroCostCalculator from "../components/landing/HeroCostCalculator";
import PackageComparisonTable from "../components/landing/PackageComparisonTable";
import AddOnsStrip from "../components/landing/AddOnsStrip";
import StickyMobileCta from "../components/landing/StickyMobileCta";
import EntityExplainer from "../components/landing/EntityExplainer";
import RatingsBadge from "../components/landing/RatingsBadge";

const steps = [
  { icon: FileText, title: "Choose a Unique LLC Name", free: true },
  { icon: UserCheck, title: "Appoint a Registered Agent", free: true },
  { icon: FilePlus, title: "File Certificate of Formation", free: false },
  { icon: BookOpen, title: "Create an Operating Agreement", free: false },
  { icon: Hash, title: "Get an EIN from the IRS", free: false },
  { icon: Landmark, title: "Open a Business Bank Account", free: false },
  { icon: Receipt, title: "Understand Texas Franchise Tax", free: false },
  { icon: Shield, title: "Get Licenses and Permits", free: false },
  { icon: CalendarCheck, title: "Stay Compliant Annually", free: false },
];

const testimonials = [
  {
    name: "Marcus J.",
    role: "E-commerce Founder",
    text: "This guide made the whole process way less intimidating. I had my LLC formed in under a week just following the steps.",
    stars: 5,
  },
  {
    name: "Sarah T.",
    role: "Freelance Designer",
    text: "I saved hundreds of dollars by doing it myself instead of hiring a service. The checklist kept me on track and the cost estimator was super helpful.",
    stars: 5,
  },
  {
    name: "David R.",
    role: "Real Estate Investor",
    text: "Clear, no-fluff instructions. The pro tips alone saved me from making costly mistakes with my registered agent and operating agreement.",
    stars: 5,
  },
];

// Inline Texas flag — renders identically across all browsers (no emoji font fallback).
function TexasFlag({ className = "h-4 w-6" }) {
  return (
    <svg viewBox="0 0 30 20" className={className} aria-hidden="true">
      <rect width="10" height="20" fill="#1E3A8A" />
      <rect x="10" width="20" height="10" fill="#ffffff" />
      <rect x="10" y="10" width="20" height="10" fill="#DC2626" />
      <polygon
        points="5,5 6.1,8.3 9.5,8.3 6.7,10.4 7.8,13.7 5,11.6 2.2,13.7 3.3,10.4 0.5,8.3 3.9,8.3"
        fill="#ffffff"
      />
    </svg>
  );
}

const roadmapPreview = [
  { title: "Prerequisites — Address & Phone", done: true, count: "2/2" },
  { title: "Choose a Unique LLC Name", done: true, count: "5/5" },
  { title: "Appoint a Registered Agent", done: true, count: "3/3" },
  { title: "File Certificate of Formation", done: false, count: "1/4", locked: false },
  { title: "Create an Operating Agreement", done: false, count: "0/3", locked: false },
];

const comparisonRows = [
  { label: "Total Cost", diy: "$0 (research only)", attorney: "$800 – $2,000+", ours: "$19 one-time" },
  { label: "Time to Complete", diy: "10+ hrs research", attorney: "2 – 4 weeks", ours: "~2 hours guided" },
  { label: "Texas-Specific Guidance", diy: "Scattered online", attorney: "Yes", ours: "100% Texas-only" },
  { label: "Keeps 100% of Fees", diy: "Yes", attorney: "No — hourly", ours: "Yes — flat $19" },
  { label: "Interactive Checklist", diy: "No", attorney: "No", ours: "Yes — 35 checkpoints" },
  { label: "Progress Saved to Cloud", diy: "No", attorney: "No", ours: "Yes — any device" },
  { label: "Lifetime Access", diy: "N/A", attorney: "One-time filing", ours: "Yes — forever" },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [searchParams] = useSearchParams();
  const [openFaq, setOpenFaq] = useState(0);

  // Capture ?ref=CODE from the URL and stash it so register can attribute the referral
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref && typeof window !== "undefined") {
      localStorage.setItem("pendingReferralCode", ref.toUpperCase().trim());
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-[var(--c-bg)] pb-20 sm:pb-0">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b-2 border-[var(--c-border)] bg-[var(--c-bg-95)] backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-lg border-2 border-[var(--c-border)] bg-white shadow-[3px_3px_0px_var(--c-shadow)]">
              <img src="/logo-mark.png" alt="Texas LLC Guide" className="h-9 w-9 object-contain" data-testid="landing-logo" />
            </div>
            <span className="font-heading text-lg font-black text-[var(--c-text)]">Texas <span className="text-[#C8102E]">LLC</span> Guide</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[2px_2px_0px_var(--c-shadow)]"
              aria-label="Toggle theme"
            >
              {dark ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-[var(--c-text)]" />}
            </button>
            <button
              data-testid="landing-login-button"
              onClick={() => navigate("/login")}
              className="rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-2 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              Sign In
            </button>
            <button
              data-testid="landing-signup-button"
              onClick={() => navigate("/login?mode=register")}
              className="rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-2 text-sm font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-1.5 text-xs font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)]">
            <TexasFlag className="h-3 w-5" />
            <span>Built for Texas Founders — Not a Generic 50-State Service</span>
          </div>
          <h1 className="font-heading text-4xl font-black tracking-tight text-[var(--c-text)] sm:text-5xl lg:text-6xl">
            Form Your Texas LLC
            <br />
            <span className="text-[#1E3A8A]">in 9 Simple Steps</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-base text-[var(--c-muted)] sm:text-lg">
            The step-by-step interactive guide that walks you through everything — from choosing your name to staying compliant. No lawyer needed.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <button
              data-testid="hero-cta-button"
              onClick={() => navigate("/login?mode=register")}
              className="flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-8 py-4 text-lg font-bold text-white shadow-[4px_4px_0px_var(--c-shadow)] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_var(--c-shadow)]"
            >
              Start Free — No Credit Card
              <ArrowRight className="h-5 w-5" />
            </button>
            <p className="text-sm text-[var(--c-muted)]">Steps 1-2 free &middot; Full guide $19</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-medium text-[var(--c-muted)]">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-[#A3E6D5]" /> Trusted by 100+ Texas founders</span>
              <span className="inline-flex items-center gap-1.5"><Shield className="h-3.5 w-3.5 text-[#A3E6D5]" /> Stripe-secured checkout</span>
              <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[#A3E6D5]" /> Founder-led · Independent</span>
              <RatingsBadge />
            </div>
          </div>
          <HeroCostCalculator />
        </div>
      </section>

      {/* Product Preview — QuickStart Roadmap */}
      <section className="border-t-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-16 sm:py-20">
        <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]">
              Your Personal Roadmap
            </p>
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">
              Track every step from idea to filed LLC.
            </h2>
            <p className="mt-4 text-base text-[var(--c-muted)]">
              Log in and get a personal dashboard showing exactly where you are in the process — with 35 interactive
              checkpoints across 10 chapters, auto-saved progress on any device, and a printable checklist you can share
              with your bank or accountant.
            </p>
            <ul className="mt-5 space-y-2">
              <li className="flex items-center gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Progress auto-syncs across your phone, tablet, and desktop</li>
              <li className="flex items-center gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Cost estimator adds up filing + agent + license fees for you</li>
              <li className="flex items-center gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="h-4 w-4 text-[#A3E6D5]" /> Downloadable completion certificate when you finish</li>
            </ul>
            <button
              data-testid="preview-cta-button"
              onClick={() => navigate("/login?mode=register")}
              className="mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-5 py-2.5 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              See Your Roadmap <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {/* Styled dashboard preview card */}
          <div className="relative">
            <div className="absolute -inset-3 -z-10 rounded-3xl bg-[#1E3A8A]/10" aria-hidden="true" />
            <div className="overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] shadow-[6px_6px_0px_var(--c-shadow)]">
              {/* Fake browser chrome */}
              <div className="flex items-center gap-1.5 border-b-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF6B6B]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#FFD93D]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#A3E6D5]" />
                <span className="ml-3 truncate text-[10px] font-medium text-[var(--c-muted)]">texasllcguide.llc/quickstart</span>
              </div>
              <div className="space-y-4 p-5">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Overall Progress</p>
                  <div className="mt-2 flex items-baseline justify-between">
                    <p className="font-heading text-2xl font-black text-[var(--c-text)]">11 of 35 done</p>
                    <p className="text-xs font-bold text-[#1E3A8A]">31%</p>
                  </div>
                  <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)]">
                    <div className="h-full rounded-full bg-[#1E3A8A]" style={{ width: "31%" }} />
                  </div>
                </div>

                <div className="space-y-2">
                  {roadmapPreview.map((step, i) => (
                    <div
                      key={step.title}
                      className={`flex items-center gap-3 rounded-lg border-2 border-[var(--c-border)] p-2.5 ${
                        step.done ? "bg-[#A3E6D5]/20" : "bg-[var(--c-surface)]"
                      }`}
                    >
                      <div
                        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border-2 border-[var(--c-border)] text-[10px] font-black ${
                          step.done ? "bg-[#1E3A8A] text-white" : "bg-[var(--c-bg)] text-[var(--c-muted)]"
                        }`}
                      >
                        {step.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i}
                      </div>
                      <p className="flex-1 truncate text-xs font-semibold text-[var(--c-text)]">{step.title}</p>
                      <p className="text-[10px] font-bold text-[var(--c-muted)]">{step.count}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Is an LLC right for me? */}
      <EntityExplainer />

      {/* What's Inside */}
      <section className="border-t-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">What&apos;s Inside</h2>
            <p className="mt-3 text-[var(--c-muted)]">9 actionable chapters with checklists, pro tips, and direct links to every resource you need.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="flex items-center gap-3 rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-4 shadow-[3px_3px_0px_var(--c-shadow)]"
                >
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A] text-sm font-bold text-white">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[var(--c-text)]">{step.title}</p>
                    {step.free && <span className="text-[10px] font-bold text-[#A3E6D5]">FREE</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t-2 border-[var(--c-border)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">Built for Action</h2>
            <p className="mt-3 text-[var(--c-muted)]">Not just information — interactive tools to keep you on track.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              { title: "Progress Tracking", desc: "Check off tasks as you go. Your progress is saved automatically.", icon: "✓" },
              { title: "Interactive Cost Estimator", desc: "Calculate your exact startup costs with our dynamic calculator.", icon: "$" },
              { title: "Step-by-Step Locking", desc: "Complete each step before moving on. No skipping, no confusion.", icon: "→" },
              { title: "Bookmarks & Notes", desc: "Save important chapters and add personal annotations.", icon: "★" },
              { title: "Completion Certificate", desc: "Get a branded PDF certificate when you finish all 9 steps.", icon: "◆" },
              { title: "Dark Mode", desc: "Easy on the eyes. Read comfortably day or night.", icon: "◑" },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]">
                <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg border-2 border-[var(--c-border)] bg-[#1E3A8A]/10 text-sm font-bold text-[#1E3A8A]">
                  {f.icon}
                </div>
                <h3 className="font-heading text-base font-bold text-[var(--c-text)]">{f.title}</h3>
                <p className="mt-1 text-sm text-[var(--c-muted)]">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free checklist lead magnet */}
      <LeadMagnetSection />

      {/* Comparison Chart */}
      <section className="border-t-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]">
              Why Texas LLC Guide
            </p>
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">
              A no-brainer next to the alternatives.
            </h2>
            <p className="mt-3 text-[var(--c-muted)]">Same outcome. A fraction of the cost — and built specifically for Texas.</p>
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] shadow-[4px_4px_0px_var(--c-shadow)] lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[var(--c-border)] bg-[var(--c-surface)]">
                  <th className="w-1/4 px-4 py-4 text-left font-heading text-xs font-bold uppercase tracking-wide text-[var(--c-muted)]"> </th>
                  <th className="w-1/4 px-4 py-4 text-left font-heading text-sm font-black text-[var(--c-text)]">DIY Alone</th>
                  <th className="w-1/4 px-4 py-4 text-left font-heading text-sm font-black text-[var(--c-text)]">Attorney</th>
                  <th className="relative w-1/4 px-4 py-4 text-left font-heading text-sm font-black text-white">
                    <div className="absolute inset-0 bg-[#1E3A8A]" aria-hidden="true" />
                    <span className="relative">Texas LLC Guide</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr
                    key={row.label}
                    className={i % 2 === 0 ? "bg-[var(--c-bg)]" : "bg-[var(--c-surface)]/60"}
                  >
                    <td className="px-4 py-3 font-semibold text-[var(--c-text)]">{row.label}</td>
                    <td className="px-4 py-3 text-[var(--c-muted)]">{row.diy}</td>
                    <td className="px-4 py-3 text-[var(--c-muted)]">{row.attorney}</td>
                    <td className="relative px-4 py-3 font-bold text-[var(--c-text)]">
                      <div className="absolute inset-0 bg-[#1E3A8A]/10" aria-hidden="true" />
                      <span className="relative flex items-center gap-1.5">
                        <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-[#1E3A8A]" /> {row.ours}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="grid gap-4 lg:hidden">
            {[
              { label: "DIY Alone", rows: comparisonRows.map((r) => [r.label, r.diy]), highlight: false, icon: <Clock className="h-4 w-4" /> },
              { label: "Attorney", rows: comparisonRows.map((r) => [r.label, r.attorney]), highlight: false, icon: <DollarSign className="h-4 w-4" /> },
              { label: "Texas LLC Guide", rows: comparisonRows.map((r) => [r.label, r.ours]), highlight: true, icon: <TexasFlag className="h-3 w-5" /> },
            ].map((col) => (
              <div
                key={col.label}
                className={`overflow-hidden rounded-2xl border-2 shadow-[4px_4px_0px_var(--c-shadow)] ${
                  col.highlight ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-[var(--c-border)] bg-[var(--c-bg)]"
                }`}
              >
                <div className={`flex items-center gap-2 border-b-2 border-[var(--c-border)] px-4 py-3 ${col.highlight ? "bg-[#1E3A8A] text-white" : "bg-[var(--c-surface)] text-[var(--c-text)]"}`}>
                  {col.icon}
                  <p className="font-heading text-sm font-black">{col.label}</p>
                </div>
                <dl className="divide-y divide-[var(--c-border-light)]">
                  {col.rows.map(([label, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-3 px-4 py-2.5">
                      <dt className="text-xs font-semibold text-[var(--c-muted)]">{label}</dt>
                      <dd className={`text-right text-xs ${col.highlight ? "font-bold text-[var(--c-text)]" : "text-[var(--c-text)]"}`}>{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-t-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">What Others Are Saying</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {testimonials.map((t) => (
              <div
                key={t.name}
                data-testid={`testimonial-${t.name}`}
                className="rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-5 shadow-[3px_3px_0px_var(--c-shadow)]"
              >
                <div className="mb-3 flex gap-0.5">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <Star key={`${t.name}-star-${si}`} className="h-4 w-4 fill-[#1E3A8A] text-[#1E3A8A]" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-[var(--c-text)]">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-4 border-t border-[var(--c-border-light)] pt-3">
                  <p className="text-sm font-bold text-[var(--c-text)]">{t.name}</p>
                  <p className="text-xs text-[var(--c-muted)]">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Story */}
      <FounderStory />

      {/* Pricing — 3 tiers */}
      <section id="pricing" className="border-t-2 border-[var(--c-border)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">Choose Your Package</h2>
            <p className="mt-3 text-[var(--c-muted)]">Start free. Upgrade when you&apos;re ready.</p>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* Free */}
            <div
              data-testid="pricing-tier-free"
              className="flex flex-col rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-left shadow-[4px_4px_0px_var(--c-shadow)]"
            >
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Free Preview</p>
              <div className="mt-2 flex items-baseline gap-1">
                <p className="font-heading text-4xl font-black text-[var(--c-text)]">$0</p>
                <p className="text-sm font-medium text-[var(--c-muted)]">forever</p>
              </div>
              <p className="mt-2 text-sm text-[var(--c-muted)]">Try the first 3 chapters before you buy.</p>
              <ul className="mt-5 flex-1 space-y-2">
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Step 0 prerequisites + Steps 1–2</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Progress tracking & bookmarks</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Cloud-synced account</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-muted)]"><X className="mt-0.5 h-4 w-4 flex-shrink-0" /> Steps 3–9 locked</li>
              </ul>
              <button
                data-testid="pricing-free-cta"
                onClick={() => navigate("/login?mode=register")}
                className="mt-6 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-5 py-3 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
              >
                Start Free
              </button>
            </div>

            {/* Premium — Recommended */}
            <div
              data-testid="pricing-tier-premium"
              className="relative flex flex-col rounded-2xl border-2 border-[#1E3A8A] bg-[#1E3A8A]/5 p-6 text-left shadow-[6px_6px_0px_var(--c-shadow)] lg:-translate-y-2"
            >
              <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white shadow-[2px_2px_0px_var(--c-shadow)]">
                <Sparkles className="h-3 w-3" /> Recommended
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">Full Guide</p>
              <div className="mt-2 flex items-baseline gap-1">
                <p className="font-heading text-4xl font-black text-[var(--c-text)]">$19</p>
                <p className="text-sm font-medium text-[var(--c-muted)]">one-time</p>
              </div>
              <p className="mt-2 text-sm text-[var(--c-muted)]">Everything you need to form your Texas LLC yourself.</p>
              <ul className="mt-5 flex-1 space-y-2">
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> All 9 steps + prerequisites unlocked</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Interactive cost estimator</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> 35 checkpoint checklist + notes</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Downloadable completion certificate</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Lifetime access & updates</li>
              </ul>
              <button
                data-testid="pricing-premium-cta"
                onClick={() => navigate("/login?mode=register")}
                className="mt-6 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-3 text-sm font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
              >
                Get the Full Guide — $19
              </button>
              <div
                data-testid="money-back-badge"
                className="mt-4 flex items-center gap-2.5 rounded-xl border-2 border-dashed border-[#1E3A8A]/50 bg-[var(--c-bg)] px-3 py-2.5"
              >
                <ShieldCheck className="h-6 w-6 flex-shrink-0 text-[#1E3A8A]" />
                <div>
                  <p className="text-xs font-black uppercase tracking-wide text-[var(--c-text)]">30-Day Money-Back Guarantee</p>
                  <p className="text-[11px] leading-snug text-[var(--c-muted)]">Not helpful? Email us within 30 days for a full refund. No questions asked.</p>
                </div>
              </div>
            </div>

            {/* Done-for-you — Best Value */}
            <div
              data-testid="pricing-tier-service"
              className="relative flex flex-col rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] p-6 text-left shadow-[4px_4px_0px_var(--c-shadow)]"
            >
              <span className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--c-text)] shadow-[2px_2px_0px_var(--c-shadow)]">
                Best Value
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--c-muted)]">Done-For-You</p>
              <div className="mt-2 flex items-baseline gap-1">
                <p className="font-heading text-4xl font-black text-[var(--c-text)]">$199</p>
                <p className="text-sm font-medium text-[var(--c-muted)]">one-time</p>
              </div>
              <p className="mt-2 text-sm text-[var(--c-muted)]">We handle the filing. You stay focused on your business.</p>
              <ul className="mt-5 flex-1 space-y-2">
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Everything in the Full Guide</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Certificate of Formation filed for you</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> Custom Operating Agreement</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> EIN application handled</li>
                <li className="flex items-start gap-2 text-sm text-[var(--c-text)]"><CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#A3E6D5]" /> 1-on-1 concierge over email</li>
              </ul>
              <button
                data-testid="pricing-service-cta"
                onClick={() => navigate("/login?mode=register")}
                className="mt-6 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-bg)] px-5 py-3 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
              >
                Get Done-For-You — $199
              </button>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-[var(--c-muted)]">
            All prices in USD &middot; State filing fee ($300) is paid directly to the Texas Secretary of State &middot; No monthly subscriptions, ever &middot; Full Guide backed by a 30-day money-back guarantee.
          </p>
        </div>
      </section>

      {/* Feature-by-feature comparison + add-ons */}
      <PackageComparisonTable onPick={(tier) => navigate(tier === "service" ? "/login?mode=register&next=service" : "/login?mode=register")} />
      <AddOnsStrip />

      {/* Founder-support callout */}
      <section className="border-t-2 border-[var(--c-border)] bg-[var(--c-surface)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl rounded-2xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] p-8 shadow-[4px_4px_0px_var(--c-shadow)]">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl border-2 border-[var(--c-border)] bg-[#1E3A8A] shadow-[3px_3px_0px_var(--c-shadow)]">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A]">Real Help From a Real Person</p>
              <h3 className="mt-1 font-heading text-2xl font-black text-[var(--c-text)]">Questions? I&apos;ll personally answer.</h3>
              <p className="mt-2 text-sm text-[var(--c-muted)]">
                Reply directly to{" "}
                <a
                  href="mailto:info@texasllcguide.llc"
                  data-testid="support-email-link"
                  className="font-bold text-[#1E3A8A] underline decoration-2 underline-offset-2"
                >
                  info@texasllcguide.llc
                </a>{" "}
                — I read every message and respond within 24 hours. No support tickets, no chatbots, no offshore call centers. Just a founder helping another founder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Landing FAQ */}
      <section className="border-t-2 border-[var(--c-border)] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl">
          <div className="mb-10 text-center">
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]">
              FAQ
            </p>
            <h2 className="font-heading text-3xl font-black text-[var(--c-text)] sm:text-4xl">
              Common questions, answered.
            </h2>
            <p className="mt-3 text-[var(--c-muted)]">Everything most Texas founders ask before they get started.</p>
          </div>

          <div className="space-y-3">
            {landingHighlights.map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={item.q}
                  data-testid={`landing-faq-item-${i}`}
                  className="overflow-hidden rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-surface)] shadow-[3px_3px_0px_var(--c-shadow)]"
                >
                  <button
                    data-testid={`landing-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-3 p-5 text-left"
                  >
                    <span className="font-heading text-sm font-semibold text-[var(--c-text)] sm:text-base">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-[var(--c-muted)] transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {isOpen && (
                    <div className="border-t-2 border-[var(--c-border-light)] px-5 pb-5 pt-3">
                      <p className="text-sm leading-relaxed text-[var(--c-muted)]">{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <button
              data-testid="landing-faq-see-all"
              onClick={() => navigate("/faq")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-5 py-2.5 text-sm font-bold text-[var(--c-text)] shadow-[3px_3px_0px_var(--c-shadow)] transition-all hover:-translate-y-0.5"
            >
              See all FAQs <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-16 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-black text-white sm:text-4xl">Ready to Start Your LLC?</h2>
          <p className="mt-3 text-blue-200">
            Join hundreds of Texas entrepreneurs who&apos;ve used this guide to launch their businesses.
          </p>
          <button
            data-testid="final-cta-button"
            onClick={() => navigate("/login?mode=register")}
            className="mt-8 inline-flex items-center gap-2 rounded-full border-2 border-white bg-white px-8 py-4 text-lg font-bold text-[#1E3A8A] shadow-[4px_4px_0px_rgba(0,0,0,0.3)] transition-all hover:-translate-y-1 hover:shadow-[6px_6px_0px_rgba(0,0,0,0.3)]"
          >
            Create Free Account
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t-2 border-[var(--c-border)] bg-[var(--c-bg)] px-4 py-8">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <p className="text-xs text-[var(--c-muted)]">&copy; {new Date().getFullYear()} Lewis Expert Enterprise — Texas LLC Guide</p>
          <div className="flex gap-4">
            <button onClick={() => navigate("/faq")} className="text-xs font-medium text-[var(--c-muted)] hover:text-[var(--c-text)]">FAQ</button>
            <button onClick={() => navigate("/login")} className="text-xs font-medium text-[var(--c-muted)] hover:text-[var(--c-text)]">Sign In</button>
          </div>
        </div>
      </footer>
      <LeadMagnetPopup />
      <StickyMobileCta />
    </div>
  );
}

